import { Color } from "./primitives";

/**
 * USED ONLY AS A FALLBACK FOR cssWithOpacity IN THE CASE WHERE RELATIVE CSS COLORS AREN'T SUPPORTED BY BROWSER
 */
export const Core = {
  surface: {
    primary: Color.grey[100],
    primaryInverse: Color.grey[900],
    secondary: Color.grey[200],
    tertiary: Color.grey[300],
    overlay: Color.transparent.grey[100],
    mask: Color.transparent.grey[500],
    neutral: Color.grey[100],
    transparent: Color.transparent.grey[0],
  },
  border: {
    subdued: Color.grey[600],
    muted: Color.grey[400],
  },
  text: {
    primary: Color.grey[900],
    subdued: Color.grey[700],
    primaryInverse: Color.grey[100],
    accent: "#ff5dd9",
  },
  icon: {
    primary: Color.grey[900],
    subdued: Color.grey[700],
    primaryInverse: Color.grey[100],
  },
} as const;

/**
 * USED ONLY AS A FALLBACK FOR cssWithOpacity IN THE CASE WHERE RELATIVE CSS COLORS AREN'T SUPPORTED BY BROWSER
 */
export const Action = {
  surface: {
    default: Color.grey[900],
    inverse: Color.grey[100],
    hover: Color.grey[1000],
    inverseHover: Color.grey[300],
    inverseActive: Color.grey[300],
    active: Color.grey[1000],
    disabled: Color.grey[300],
    neutral: Color.grey[900],
    neutralHover: Color.grey[900],
    neutralActive: Color.grey[900],
    favorited: Color.red[700],
    critical: Color.red[100],
    criticalHover: Color.red[700],
    criticalInverseHover: Color.red[100],
    criticalActive: Color.red[700],
    criticalInverseActive: Color.red[100],
  },
  border: {
    default: Color.grey[900],
    inverse: Color.grey[100],
    subdued: Color.grey[400],
    hover: Color.grey[1000],
    active: Color.grey[1000],
    disabled: Color.grey[400],
    focus_ring: Color.blue[700],
    critical: Color.red[500],
  },
  text: {
    default: Color.grey[900],
    inverse: Color.grey[100],
    hover: Color.grey[1000],
    inverseHover: Color.grey[1000],
    active: Color.grey[1000],
    inverseActive: Color.grey[1000],
    disabled: Color.grey[600],
    placeholder: Color.grey[700],
    critical: Color.red[700],
    criticalHover: Color.red[800],
    criticalInverse: Color.grey[100],
    criticalActive: Color.red[800],
  },
  icon: {
    default: Color.grey[900],
    inverse: Color.grey[100],
    hover: Color.grey[1000],
    inverseHover: Color.grey[1000],
    active: Color.grey[1000],
    inverseActive: Color.grey[1000],
    disabled: Color.grey[600],
    critical: Color.red[700],
    criticalHover: Color.red[800],
    criticalInverse: Color.grey[100],
    criticalActive: Color.red[800],
  },
} as const;

/**
 * USED ONLY AS A FALLBACK FOR cssWithOpacity IN THE CASE WHERE RELATIVE CSS COLORS AREN'T SUPPORTED BY BROWSER
 */
export const Message = {
  text: {
    critical: Color.red[700],
    sale: Color.orange[1000],
  },
  icon: {
    success: Color.green[700],
    warning: Color.yellow[800],
    critical: Color.red[700],
    info: Color.blue[1000],
  },
  border: {
    success: Color.green[600],
    warning: Color.yellow[400],
    critical: Color.red[500],
    info: Color.blue[400],
  },
  surface: {
    success: Color.green[100],
    warning: Color.yellow[100],
    critical: Color.red[100],
    info: Color.blue[200],
  },
} as const;

/**
 * USED ONLY AS A FALLBACK FOR cssWithOpacity IN THE CASE WHERE RELATIVE CSS COLORS AREN'T SUPPORTED BY BROWSER
 */
export const Expressive = {
  surface: {
    neutral: Color.neutral[100],
    neutralInverse: Color.neutral[1000],
    yellow: Color.yellow[200],
    yellowInverse: Color.yellow[1000],
    green: Color.green[300],
    greenInverse: Color.green[1000],
    blue: Color.blue[200],
    blueInverse: Color.blue[1000],
    red: Color.red[200],
    redInverse: Color.red[1000],
    orange: Color.orange[100],
    orangeInverse: Color.orange[400],
    olive: "#595604",
    eggplant: "#41252a",
    earth: "#453200",
  },
  icon: {
    neutral: Color.neutral[1000],
    neutralInverse: Color.neutral[100],
    yellow: Color.yellow[1000],
    yellowInverse: Color.yellow[200],
    green: Color.green[1000],
    greenInverse: Color.green[300],
    blue: Color.blue[1000],
    blueInverse: Color.blue[200],
    red: Color.red[1000],
    redInverse: Color.red[200],
    orange: Color.orange[1000],
    orangeInverse: Color.orange[100],
  },
  text: {
    neutral: Color.neutral[1000],
    neutralInverse: Color.neutral[100],
    yellow: Color.yellow[1000],
    yellowInverse: Color.yellow[200],
    green: Color.green[1000],
    greenInverse: Color.green[300],
    blue: Color.blue[1000],
    blueInverse: Color.blue[200],
    red: Color.red[1000],
    redInverse: Color.red[200],
    orange: Color.orange[400],
    orangeInverse: Color.orange[100],
  },
} as const;

/**
 * USED ONLY AS A FALLBACK FOR cssWithOpacity IN THE CASE WHERE RELATIVE CSS COLORS AREN'T SUPPORTED BY BROWSER
 */
export const Program = {
  surface: {
    insider: {
      primary: Color.blue[100],
      primaryInverse: Color.teal[900],
    },
    top_shop: {
      primary: Color.green[200],
      primaryInverse: Color.green[1000],
      secondary: Color.green[500],
    },
    faire_source: {
      primary: "#f6f2e8",
      primaryInverse: "#735b21",
    },
    faire_pay: {
      primary: "#e9eefb",
      primaryInverse: "#213871",
      secondary: "#365cbb",
    },
    ai_suggestions: {
      primary: "#f7e9ff",
      primaryInverse: "#eacfff",
    },
    market: {
      primary: "#56160C",
      primaryInverse: "#F8F1E9",
      secondary: "#F38F7F",
      tertiary: "#DF6630",
    },
    faire_direct: {
      primary: "#e8eeff",
      primaryInverse: "#a2b8ff",
      secondary: "#fbfaf9",
      secondaryInverse: "#f6f5f2",
      tertiary: "#fe8240",
    },
  },
  text: {
    insider: Color.teal[900],
    top_shop: Color.green[1000],
    faire_source: "#735B21",
    faire_pay: "#365cbb",
  },
  icon: {
    insider: Color.teal[600],
    top_shop: Color.green[500],
    faire_source: "#735B21",
    market: "#DF6630",
    faire_pay: "#365cbb",
  },
  border: {
    ai_suggestions: "#eacfff",
  },
} as const;

/**
 * USED ONLY AS A FALLBACK FOR cssWithOpacity IN THE CASE WHERE RELATIVE CSS COLORS AREN'T SUPPORTED BY BROWSER
 */
export const Button = {
  surface: {
    secondary: Color.grey[100],
    secondaryHover: Color.grey[300],
    secondaryActive: Color.grey[300],
    tertiary: Color.transparent.grey[0],
    tertiaryHover: Color.grey[300],
    tertiaryActive: Color.grey[300],
  },
  border: {
    secondary: Color.grey[400],
    secondaryHover: Color.grey[1000],
    secondaryActive: Color.grey[1000],
  },
} as const;

/**
 * USED ONLY AS A FALLBACK FOR cssWithOpacity IN THE CASE WHERE RELATIVE CSS COLORS AREN'T SUPPORTED BY BROWSER
 */
export const Tag = {
  surface: {
    default: Color.grey[300],
    hover: Color.grey[400],
    active: Color.grey[400],
    strong: Color.grey[800],
  },
} as const;

/**
 * USED ONLY AS A FALLBACK FOR cssWithOpacity IN THE CASE WHERE RELATIVE CSS COLORS AREN'T SUPPORTED BY BROWSER
 */
export const Toggle = {
  surface: {
    default: Color.grey[400],
    inverse: Color.grey[1000],
    subdued: Color.grey[100],
    disabled: Color.grey[300],
  },
  border: {
    disabled: Color.grey[400],
  },
} as const;

/**
 * USED ONLY AS A FALLBACK FOR cssWithOpacity IN THE CASE WHERE RELATIVE CSS COLORS AREN'T SUPPORTED BY BROWSER
 */
export const ProgressBar = {
  surface: {
    default: Color.grey[400],
  },
} as const;

export default {
  Action,
  Button,
  Core,
  Expressive,
  Message,
  ProgressBar,
  Program,
  Tag,
  Toggle,
} as const;
